// Оболочка кабинета: шапка, две колонки, выдвижное меню в адаптиве.
//
// Раскладка под ширину окна, а не под рамку устройства. Макеты отданы в
// iPhone 402×874, но выпускается веб, и мобильная колонка посреди пустого
// десктопа выглядит недоделкой; design/README.md прямо разрешает
// воспроизводить экраны на паттернах платформы, и здесь это применено
// буквально: те же токены, копирайт и знак — раскладка своя.
//
// Безопасных зон под статус-бар и home indicator тут нет: в браузере их не
// существует. Они лежат в MOBILE из @smyslika/ui-tokens и достаются в P9.
//
// ⚠️ ПРАВОЙ КОЛОНКИ БОЛЬШЕ НЕТ (06.09.2026). В ней жили источники, состав
// результата и недавние задачи — и она же сжимала разговор в узкую полосу.
// Источники переехали под ответ свёрнутым списком (`Sources` в ui/data.tsx):
// там они читаются как часть ответа, а не как мебель вокруг него. Оболочка
// стала на одну колонку проще, и это её главное свойство: она ничего не знает
// про содержимое экрана.

import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'

import { freeFollowupsPromise, SCENARIO_COPY, UNIT_PLURALS } from '@smyslika/core'
import { attempts, type ChatSummary } from '@smyslika/api-client'

import { useActiveRun } from '../active-run.tsx'
import { useCompact } from '../breakpoint.ts'
import { useSession } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'
import { isTaskAvailable, TASKS, UNAVAILABLE_SHORT } from '@smyslika/ui-tokens'
import { Confirm, Icon, Mark, ProgressBar, Sheet } from '../ui/index.ts'

export { TASKS, taskBySlug } from '@smyslika/ui-tokens'

/**
 * Сколько чатов видно в колонке. Дальше — «Все чаты».
 *
 * Пять, а не двенадцать: колонка нужна, чтобы вернуться к тому, чем человек
 * занят сейчас, а не чтобы листать в ней историю. Для истории есть свой
 * экран, и он умеет искать.
 */
const CHATS_SHOWN = 5

function NavRows({ onNavigate }: { onNavigate?: () => void }) {
  const { chats, refresh } = useWorkspace()
  const { chats: api, reference } = useSession()
  const navigate = useNavigate()
  const location = useLocation()

  /**
   * Убрать или изменить чат.
   *
   * Список перечитывается после каждого действия, а не правится на месте:
   * порядок задаёт сервер (закреплённые сверху, потом свежие), и повторить
   * его здесь значило бы завести вторую сортировку, которая разойдётся с
   * первой в день первой правки.
   */
  const act = async (id: string, what: 'pin' | 'unpin' | 'archive' | 'delete'): Promise<void> => {
    try {
      if (what === 'delete') {
        await api.remove(id)
        // Попытке удалённого чата возвращаться некуда (SMY-449).
        attempts.forgetChat(id)
      } else if (what === 'archive') await api.update(id, { archived: true })
      else await api.update(id, { pinned: what === 'pin' })
    } catch {
      // Колонка — не то, ради чего стоит ронять экран. Не получилось — список
      // просто останется прежним.
    }
    await refresh()
    // Открытый чат исчез из-под ног: уводим в новый, иначе человек смотрит на
    // ленту того, что только что удалил.
    if ((what === 'delete' || what === 'archive') && location.pathname === `/chat/${id}`) navigate('/')
  }

  return (
    <nav className="nav">
      {/* «Новый запрос», а не «Новый чат». Чат — это то, что остаётся ПОСЛЕ:
          переписка, которую видно в списке ниже. Кнопка же начинает не
          переписку, а работу, и называть её именем следа этой работы значило
          звать человека завести пустую папку. Подпись одна на обе платформы —
          в мобильном меню она же. */}
      <NavLink to="/" end className="nav__new" onClick={onNavigate}>
        Новый запрос
        <span className="nav__hotkey">⌘K</span>
      </NavLink>

      <div className="nav-group">Задачи</div>
      {TASKS.map((t) =>
        // Выключенный сценарий гаснет и здесь, а не только в сетке на главном:
        // иначе левая колонка вела бы туда, откуда главный экран уводит.
        // Именно строкой, а не пропажей: исчезнувший пункт меню выглядит
        // поломкой, а приглушённый — ограничением.
        isTaskAvailable(reference, t.scenario) ? (
          <NavLink key={t.slug} to={`/task/${t.slug}`} className="nav-row" onClick={onNavigate}>
            <Icon name={t.icon} size={18} />
            {SCENARIO_COPY[t.scenario].card}
          </NavLink>
        ) : (
          // Имя собрано целиком, а не сложено из кусков разметки. `title`
          // здесь стоять не может вовсе: программа чтения с экрана берёт его
          // ИМЕНЕМ строки и читает «Сейчас не запускается» вместо «Найти» —
          // человек слышит состояние и не слышит, чего оно касается. Из
          // видимых кусков вышло бы «Найти пока нет», что не лучше.
          <span
            key={t.slug}
            className="nav-row nav-row--off"
            aria-disabled="true"
            aria-label={`${SCENARIO_COPY[t.scenario].card} — ${UNAVAILABLE_SHORT.toLowerCase()}`}
          >
            <Icon name={t.icon} size={18} />
            {SCENARIO_COPY[t.scenario].card}
            <span className="nav__count">пока нет</span>
          </span>
        ),
      )}

      {/* Проектов здесь больше нет (06.09.2026). Папка вокруг цели оказалась
          лишним уровнем: чат заводится на каждый вопрос и называется этим
          вопросом, а раскладывать их по папкам никто не стал. Сервер про
          проекты ещё знает — вернуть недолго, — но выбирать между «чатом» и
          «проектом» человек больше не должен. */}
      <div className="nav-group">Чаты</div>
      {chats.slice(0, CHATS_SHOWN).map((c) => (
        <ChatRow key={c.id} chat={c} onNavigate={onNavigate} onAct={act} />
      ))}
      {chats.length === 0 ? (
        <p className="nav__empty">Каждый новый вопрос заводит свой чат — они появятся здесь.</p>
      ) : null}
      {chats.length > CHATS_SHOWN ? (
        <NavLink to="/history" className="nav-row nav-row--more" onClick={onNavigate}>
          Все чаты
        </NavLink>
      ) : null}

      {/* «Лимиты» отсюда убраны 06.09.2026: строка показывала то одну
          категорию, то другую — ту, которой меньше осталось, — и читалась как
          случайное число. Все остатки и так стоят в шапке, а разбор списаний
          живёт в профиле. */}
      <div className="nav__bottom">
        <NavLink to="/profile" className="nav-row" onClick={onNavigate}>
          <Icon name="profile" size={18} />
          Профиль и настройки
        </NavLink>
      </div>
    </nav>
  )
}

/**
 * Остатки — одной кнопкой, а не рядом плашек. SMY-116
 *
 * Ряд из трёх плашек занимал половину шапки и заставлял читать три числа там,
 * где нужен один ответ: «много осталось или мало». Теперь в шапке полоса и
 * общий остаток, а разбивка по единицам открывается наведением.
 *
 * ОБЩАЯ ПОЛОСА СКЛАДЫВАЕТ РАЗНОЕ, и это осознанно. Вопрос, документ и
 * изображение — единицы несопоставимые, их сумма ничего не значит сама по
 * себе. Но полоса и не обещает значить: она отвечает на «пора ли
 * беспокоиться», а на этот вопрос доля выработанного отвечает верно. Точные
 * числа — в раскрытии, на расстоянии одного движения.
 */
/**
 * Строка чата с меню действий.
 *
 * Меню открывается кнопкой, а не правым щелчком: контекстное меню браузера
 * человек не ждёт внутри приложения, а на телефоне его нет вовсе.
 *
 * Удаление спрашивает подтверждение, архив — нет. Разница в обратимости:
 * архив возвращается из истории одним нажатием, а переписку восстанавливать
 * неоткуда.
 */
function ChatRow({
  chat,
  onNavigate,
  onAct,
}: {
  chat: ChatSummary
  onNavigate?: (() => void) | undefined
  onAct: (id: string, what: 'pin' | 'unpin' | 'archive' | 'delete') => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [ask, setAsk] = useState<'archive' | 'delete' | null>(null)

  useEffect(() => {
    if (!open) return
    // Клик мимо и Esc закрывают меню. Без этого оно остаётся висеть поверх
    // колонки, пока не нажмут ровно ту же кнопку.
    const close = (): void => setOpen(false)
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('click', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  /**
   * Что спросить перед действием.
   *
   * Удаление и архив спрашивают, закрепление — нет: первые два убирают чат
   * из-под глаз, а закрепление только меняет порядок и снимается тем же
   * жестом. Формулировки разные не для красоты — из архива чат возвращается,
   * из удаления нет, и человек должен видеть разницу до нажатия.
   */
  const ASK: Record<'archive' | 'delete', { title: string; text: string; action: string; danger?: boolean }> = {
    archive: {
      title: 'Убрать чат в архив?',
      text: 'Он исчезнет из колонки слева, но останется в истории — оттуда его можно вернуть.',
      action: 'В архив',
    },
    delete: {
      title: 'Удалить чат?',
      text: 'Вместе с ним удалится вся переписка, и восстановить её будет неоткуда. История списаний останется.',
      action: 'Удалить',
      danger: true,
    },
  }

  const act = (what: 'pin' | 'unpin' | 'archive' | 'delete') => (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(false)
    if (what === 'archive' || what === 'delete') {
      setAsk(what)
      return
    }
    void onAct(chat.id, what)
  }

  return (
    <div className={`nav-row nav-row--chat${open ? ' nav-row--menu' : ''}`}>
      <NavLink to={`/chat/${chat.id}`} className="nav-row__link" onClick={onNavigate}>
        {chat.pinned === true ? <Icon name="pin" size={14} strokeWidth={1.9} /> : null}
        <span className="nav-row__text">{chat.title ?? 'Без названия'}</span>
      </NavLink>

      <button
        type="button"
        className="nav-row__more"
        aria-label={`Что сделать с чатом «${chat.title ?? 'без названия'}»`}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        <Icon name="more" size={16} />
      </button>

      {open ? (
        <div className="nav-menu" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={act(chat.pinned === true ? 'unpin' : 'pin')}>
            {chat.pinned === true ? 'Открепить' : 'Закрепить'}
          </button>
          <button type="button" onClick={act('archive')}>
            В архив
          </button>
          <button type="button" className="nav-menu__danger" onClick={act('delete')}>
            Удалить
          </button>
        </div>
      ) : null}

      {ask != null ? (
        <Confirm
          open
          title={ASK[ask].title}
          action={ASK[ask].action}
          {...(ASK[ask].danger === true ? { danger: true } : {})}
          onCancel={() => setAsk(null)}
          onConfirm={() => {
            setAsk(null)
            void onAct(chat.id, ask)
          }}
        >
          {ASK[ask].text}
        </Confirm>
      ) : null}
    </div>
  )
}

function Limits() {
  const { balances } = useSession()
  const [open, setOpen] = useState(false)
  if (balances == null) return null

  const rows = balances.balances.filter((b) => b.limit > 0)
  if (rows.length === 0) return null

  const remaining = rows.reduce((n, b) => n + b.remaining, 0)
  const limit = rows.reduce((n, b) => n + b.limit, 0)
  // Тревожно, если тревожна хоть одна категория: полоса складывает разное, и
  // усреднённая доля спрятала бы кончающиеся документы за нетронутыми
  // вопросами. Порог считает сервер — второе число здесь было бы вторым
  // источником правды.
  const low = rows.some((b) => b.low)

  return (
    <div
      className="limits-btn"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={`limits-btn__face${low ? ' limits-btn__face--warn' : ''}`}
        // Наведения нет на телефоне, и кнопка обязана открываться нажатием.
        // `aria-expanded` — то же состояние для программы чтения с экрана.
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="limits-btn__label">Лимиты</span>
        <span className="limits-btn__value">
          {remaining} из {limit}
        </span>
        <ProgressBar value={remaining} max={limit} low={low} />
      </button>

      {open ? (
        <div className="limits-pop" role="group" aria-label="Остатки по категориям">
          {/* Объяснение рядом с числами, а не в справке: вопрос «а что это
              вообще такое» возникает ровно здесь, когда человек смотрит на
              «142 из 150» и не понимает, много это или мало. */}
          {rows.map((b) => (
            <div key={b.unit} className="limits-pop__row">
              <span className="limits-pop__name">
                {UNIT_PLURALS[b.unit].charAt(0).toUpperCase() + UNIT_PLURALS[b.unit].slice(1)}
              </span>
              <span className={`limits-pop__value${b.low ? ' limits-pop__value--warn' : ''}`}>
                {b.remaining} из {b.limit}
              </span>
              <ProgressBar value={b.remaining} max={b.limit} low={b.low} />
            </div>
          ))}
          <NavLink to="/limits" className="limits-pop__more" onClick={() => setOpen(false)}>
            Управление лимитами
          </NavLink>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Что такое лимиты — человеческими словами.
 *
 * Отдельным окном, а не абзацем в раскрытии: там числа, и длинное объяснение
 * рядом с ними мешает тем, кто пришёл просто посмотреть остаток. Текст
 * намеренно без слов «единица», «тариф», «квота» — их и объясняем.
 */
function WhatAreLimits({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} label="Что такое лимиты" closable>
      <h2 className="confirm__title">Что такое лимиты</h2>

      <div className="explain">
        <p>
          Лимит — это сколько задач я могу сделать для тебя за месяц. Задал вопрос, получил ответ — одна
          задача. Ничего заранее покупать и настраивать не нужно, всё уже включено.
        </p>
        <p>
          Счётчиков три, потому что задачи разные по сложности. <strong>Вопросы</strong> — всё, что ты
          спрашиваешь словами: объяснить, найти в интернете, проверить, написать текст. <strong>Документы</strong> —
          когда я разбираю твой файл или собираю новый. <strong>Изображения</strong> — когда рисую картинку.
        </p>
        <p>
          Считается только <strong>готовый результат</strong>. Если я переспросил, что-то не получилось или ты
          отменил на полпути — с лимита не списывается ничего. {freeFollowupsPromise()}
        </p>
        <p>
          Раз в месяц счётчики обнуляются и начинаются заново — дата видна на экране лимитов.
          Неизрасходованное не переносится, а если чего-то не хватило, можно докупить или перейти на тариф
          побольше.
        </p>
      </div>

      <div className="confirm__actions">
        <button type="button" className="btn btn--primary" onClick={onClose}>
          Понятно
        </button>
      </div>
    </Sheet>
  )
}

/**
 * Состояние связи показывается всегда, когда оно не «в порядке».
 *
 * Молчаливое замирание — худший вариант: пользователю обещали, что можно
 * закрыть вкладку, и он должен знать, продолжает ли обещание действовать.
 */
function ConnectionBar() {
  const { connection, run } = useActiveRun()
  if (connection === 'online') return null

  const running = run != null && run.status === 'running'
  // «Нет связи», а не «нет сети»: та же беда тем же словом, что в строках
  // экрана чата под полосой, — два названия одного обрыва читались бы двумя
  // бедами (решение владельца 06.10.2026, SMY-449). «Вернусь», а не
  // «вернёмся»: экран говорит от «я», как в чате (решение владельца 07.10.2026).
  return (
    <div className={`connection connection--${connection}`} role="status">
      <span className="connection__dot" />
      {connection === 'offline'
        ? running
          ? 'Нет связи. Задача продолжается на сервере — вернусь к ней, как только связь появится.'
          : 'Нет связи.'
        : 'Связь прервалась, подключаюсь заново…'}
    </div>
  )
}

/**
 * Четыре раздела аккаунта. Порядок из веб-макета: сначала то, что человек
 * делал, потом то, за что платит, потом сколько осталось, потом он сам.
 */
export const ACCOUNT_SECTIONS = [
  { to: '/history', title: 'История чатов' },
  { to: '/tariff', title: 'Тарифы и оплата' },
  { to: '/limits', title: 'Лимиты и списания' },
  { to: '/profile', title: 'Профиль и данные' },
]

/**
 * Левая колонка на экранах аккаунта.
 *
 * Она не дополняет навигацию задач, а ЗАМЕНЯЕТ её — так в макете W07–W10.
 * Держать оба списка сразу значило бы предлагать начать задачу с экрана, на
 * который человек пришёл разбираться с деньгами.
 */
function AccountRows({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="nav">
      <NavLink to="/" className="nav__back" onClick={onNavigate}>
        <Icon name="back" size={18} />В чат
      </NavLink>

      <div className="nav-group">Аккаунт</div>
      {ACCOUNT_SECTIONS.map((s) => (
        <NavLink key={s.to} to={s.to} className="nav-row" onClick={onNavigate}>
          {s.title}
        </NavLink>
      ))}
    </nav>
  )
}

export function Shell({
  children,
  account,
}: {
  children: ReactNode
  /** Экран аккаунта: левая колонка становится подменю. */
  account?: boolean
}) {
  const { tariff } = useSession()
  const compact = useCompact()
  const [drawer, setDrawer] = useState(false)
  const [explain, setExplain] = useState(false)
  const location = useLocation()

  // Переход закрывает меню. Иначе оно осталось бы поверх нового экрана.
  useEffect(() => setDrawer(false), [location.pathname])

  useEffect(() => {
    if (!drawer) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawer(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawer])

  return (
    <div className="shell">
      <header className="header">
        <button className="header__menu" onClick={() => setDrawer(true)} aria-label="Меню">
          <Icon name="menu" size={22} />
        </button>
        <NavLink to="/" className="header__lockup">
          <Mark height={17} />
          <span className="header__word">смыслика</span>
        </NavLink>
        {tariff === 'plus' || tariff === 'pro' ? (
          <span className="header__tag">{tariff === 'pro' ? 'Про' : 'Плюс'}</span>
        ) : null}

        <span className="header__spacer" />
        {/* На узком экране ряд остатков уезжает под шапку — так в макете m01.
            Держать его здесь значило бы сжать плашки в полосу, по которой не
            попасть пальцем.

            Аватара и кнопки «Выйти» справа больше нет (06.09.2026): обе вели
            туда же, куда «Профиль и настройки» в левой колонке, и повторяли
            её на каждом экране. Один путь к профилю — один. */}
        {compact ? null : (
          <>
            <Limits />
            <button
              type="button"
              className="header__what"
              onClick={() => setExplain(true)}
            >
              Что такое лимиты
            </button>
          </>
        )}
      </header>

      <div className="shell__body">
        <aside className="shell__nav">{account === true ? <AccountRows /> : <NavRows />}</aside>

        <main className="shell__main">
          <ConnectionBar />
          {compact ? (
            <div className="shell__balances">
              <Limits />
              <button type="button" className="header__what" onClick={() => setExplain(true)}>
                Что такое лимиты
              </button>
            </div>
          ) : null}
          {children}
        </main>
      </div>

      <WhatAreLimits open={explain} onClose={() => setExplain(false)} />

      {drawer ? (
        <div className="drawer">
          <button className="drawer__scrim" aria-label="Закрыть меню" onClick={() => setDrawer(false)} />
          <div className="drawer__panel">
            {account === true ? (
              <AccountRows onNavigate={() => setDrawer(false)} />
            ) : (
              <NavRows onNavigate={() => setDrawer(false)} />
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
